import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import {
  collection, onSnapshot, orderBy, query, doc, updateDoc, deleteDoc
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import AdminCompliance from "../components/AdminCompliance";
import { ADMIN_DRIVERS, CUSTOMER_INVOICES } from "../data/mock";

const STATUS_BADGE = {
  out: { label: "Out for delivery", className: "bg-[#f59321]/15 text-[#c46905]" },
  delivered: { label: "Delivered", className: "bg-emerald-100 text-emerald-700" },
  pending: { label: "Pending", className: "bg-amber-100 text-amber-700" },
  assigned: { label: "Assigned", className: "bg-blue-100 text-blue-700" },
  picked: { label: "Picked up", className: "bg-indigo-100 text-indigo-700" },
  cancelled: { label: "Cancelled", className: "bg-red-100 text-red-700" }
};
const STATUS_ORDER = ["pending", "assigned", "picked", "out", "delivered", "cancelled"];

const APP_STATUS_BADGE = {
  pending: { label: "Pending review", className: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", className: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-700" }
};

const DRIVER_STATUS = {
  "On route": "bg-[#f59321]/15 text-[#c46905]",
  Available: "bg-emerald-100 text-emerald-700",
  "On break": "bg-amber-100 text-amber-700",
  "Off duty": "bg-slate-200 text-slate-600"
};

// Convert Firestore Timestamp -> readable string
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState([]);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchOrder, setSearchOrder] = useState("");

  // Live subscriptions
  useEffect(() => {
    const q = query(collection(db, "requests"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn("requests read error:", err?.message)
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "driver_applications"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => setApplications(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn("applications read error:", err?.message)
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn("users read error (needs admin list permission):", err?.message)
    );
    return () => unsub();
  }, []);

  // Derived stats
  const stats = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todaysDeliveries = requests.filter((r) => {
      const t = r.createdAt?.toDate ? r.createdAt.toDate() : null;
      return t && t >= today;
    }).length;
    const pending = requests.filter((r) => r.status === "pending").length;
    const driversCount = users.filter((u) => u.role === "driver").length;
    const customersCount = users.filter((u) => u.role === "customer" || !u.role).length;
    return { todaysDeliveries, pending, driversCount, customersCount };
  }, [requests, users]);

  const updateRequestStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, "requests", id), { status });
      toast({ title: "Status updated", description: `Order marked "${STATUS_BADGE[status]?.label || status}".` });
    } catch (err) {
      toast({ title: "Couldn't update", description: err?.message || "Check Firestore rules." });
    }
  };

  const updateApplicationStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, "driver_applications", id), { status });
      toast({ title: "Application updated", description: `Marked as ${status}.` });
    } catch (err) {
      toast({ title: "Couldn't update", description: err?.message || "Check Firestore rules." });
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Delete this delivery request permanently?")) return;
    try {
      await deleteDoc(doc(db, "requests", id));
      toast({ title: "Deleted" });
    } catch (err) {
      toast({ title: "Couldn't delete", description: err?.message });
    }
  };

  const filteredRequests = requests.filter((r) => {
    if (!searchOrder) return true;
    const s = searchOrder.toLowerCase();
    return (
      (r.id || "").toLowerCase().includes(s) ||
      (r.name || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.company || "").toLowerCase().includes(s) ||
      (r.pickup || "").toLowerCase().includes(s) ||
      (r.dropoff || "").toLowerCase().includes(s)
    );
  });

  const kpiCards = [
    { label: "Today's requests", value: stats.todaysDeliveries, icon: "Truck", change: `${requests.length} total` },
    { label: "Pending review", value: stats.pending, icon: "Clock", change: "Live" },
    { label: "Registered drivers", value: stats.driversCount, icon: "Users", change: `${applications.filter(a => a.status === "pending" || !a.status).length} applying` },
    { label: "Customers", value: stats.customersCount, icon: "UserCheck", change: "Total accounts" }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-[#0f2847] text-white stripe-bg border-b border-[#0f2847]">
        <div className="container-518 py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-1">
                Admin · Control room
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-extrabold">
                Dispatch overview
              </h1>
              <p className="mt-1 text-slate-300">Signed in as {user?.email || "admin"} · Live data refreshes every 30s.</p>
            </div>
            <div className="flex gap-3">
              <a href="/admin/deliveries">
                <Button className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-11 px-5 font-semibold btn-lift" data-testid="admin-deliveries-link">
                  <Icons.Truck className="w-4 h-4 mr-1.5" />
                  Deliveries
                </Button>
              </a>
              <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-11 px-5 font-semibold">
                <Icons.Download className="w-4 h-4 mr-1.5" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="container-518 py-8">
        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Activity;
            return (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-200 card-lift">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl bg-[#0f2847] text-white flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-bold text-slate-500">{s.change}</span>
                </div>
                <div className="font-display text-3xl font-extrabold text-[#0f2847]">{s.value}</div>
                <div className="mt-1 text-sm text-slate-500 font-medium">{s.label}</div>
              </div>
            );
          })}
        </div>

        <Tabs defaultValue="orders" className="mt-8">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-full h-12 flex-wrap">
            <TabsTrigger value="orders" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Live requests <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#f59321] text-white text-[10px]">{requests.length}</span>
            </TabsTrigger>
            <TabsTrigger value="applications" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Driver applications <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#f59321] text-white text-[10px]">{applications.length}</span>
            </TabsTrigger>
            <TabsTrigger value="drivers" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Drivers
            </TabsTrigger>
            <TabsTrigger value="revenue" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Revenue
            </TabsTrigger>
            <TabsTrigger value="customers" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Customers <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px]">{users.length}</span>
            </TabsTrigger>
            <TabsTrigger value="compliance" className="rounded-full px-5 h-10 font-semibold data-[state=active]:bg-[#0f2847] data-[state=active]:text-white">
              Compliance
            </TabsTrigger>
          </TabsList>

          {/* LIVE REQUESTS */}
          <TabsContent value="orders" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-display font-bold text-[#0f2847]">Delivery requests (live from Firestore)</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Updates in real-time when a client submits a form.</p>
                </div>
                <div className="relative">
                  <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchOrder}
                    onChange={(e) => setSearchOrder(e.target.value)}
                    placeholder="Search by name, address, email…"
                    className="h-10 pl-9 w-72 rounded-full border-slate-200 text-sm"
                  />
                </div>
              </div>

              {filteredRequests.length === 0 ? (
                <div className="p-16 text-center">
                  <Icons.Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-display font-bold text-[#0f2847]">
                    {requests.length === 0 ? "No requests yet" : "No matches"}
                  </h4>
                  <p className="text-sm text-slate-500 mt-1">
                    {requests.length === 0
                      ? "Delivery requests from the Get a Quote form will appear here in real time."
                      : "Try a different search term."}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">
                      <tr>
                        <th className="px-6 py-3">Received</th>
                        <th className="px-6 py-3">Customer</th>
                        <th className="px-6 py-3">Route</th>
                        <th className="px-6 py-3">Service</th>
                        <th className="px-6 py-3">Status</th>
                        <th className="px-6 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((r) => {
                        const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                        return (
                          <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                            <td className="px-6 py-4">
                              <div className="text-xs font-bold text-[#0f2847]">{fmtDate(r.createdAt)}</div>
                              <div className="text-[10px] text-slate-400 mt-0.5 font-mono">#{r.id.slice(0, 8)}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-bold text-[#0f2847]">{r.name || "—"}</div>
                              <div className="text-xs text-slate-500">{r.company || ""}</div>
                              {r.email && (
                                <a href={`mailto:${r.email}`} className="text-xs text-[#0f2847] hover:text-[#f59321] block mt-0.5 truncate max-w-[180px]">
                                  {r.email}
                                </a>
                              )}
                              {r.phone && (
                                <a href={`tel:${r.phone.replace(/[^0-9]/g, "")}`} className="text-xs text-slate-600 hover:text-[#f59321] block">
                                  {r.phone}
                                </a>
                              )}
                            </td>
                            <td className="px-6 py-4 text-slate-700">
                              <div className="text-xs"><span className="font-bold text-emerald-600">From:</span> {r.pickup || "—"}</div>
                              <div className="text-xs mt-1"><span className="font-bold text-red-600">To:</span> {r.dropoff || "—"}</div>
                              {r.instructions && (
                                <div className="text-[11px] text-slate-500 italic mt-2 max-w-[240px]">
                                  "{r.instructions.slice(0, 80)}{r.instructions.length > 80 ? "…" : ""}"
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-slate-700">
                              <div className="text-xs font-semibold">{r.packageType || "—"}</div>
                              <div className="text-[11px] text-slate-500 capitalize">{r.serviceLevel || "—"}</div>
                            </td>
                            <td className="px-6 py-4">
                              <Select
                                value={r.status || "pending"}
                                onValueChange={(v) => updateRequestStatus(r.id, v)}
                              >
                                <SelectTrigger className={`h-8 rounded-full border-0 text-xs font-bold px-3 ${s.className} focus:ring-2 focus:ring-[#f59321]`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_ORDER.map((k) => (
                                    <SelectItem key={k} value={k}>{STATUS_BADGE[k].label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => deleteRequest(r.id)}
                                title="Delete"
                                className="text-slate-400 hover:text-red-600 transition"
                              >
                                <Icons.Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* DRIVER APPLICATIONS */}
          <TabsContent value="applications" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-display font-bold text-[#0f2847]">Driver applications (live)</h3>
                <p className="text-xs text-slate-500 mt-0.5">New driver signups from /driver-signup form.</p>
              </div>
              {applications.length === 0 ? (
                <div className="p-16 text-center">
                  <Icons.UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-display font-bold text-[#0f2847]">No applications yet</h4>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 p-5">
                  {applications.map((a) => {
                    const badge = APP_STATUS_BADGE[a.status] || APP_STATUS_BADGE.pending;
                    return (
                      <div key={a.id} className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="font-display font-bold text-[#0f2847] text-lg">{a.firstName} {a.lastName}</div>
                            <div className="text-xs text-slate-500">{fmtDate(a.createdAt)}</div>
                          </div>
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${badge.className}`}>
                            {badge.label}
                          </span>
                        </div>
                        <div className="space-y-1.5 text-xs text-slate-700 mb-4">
                          <div><span className="font-bold">📍</span> {a.city}</div>
                          <div><span className="font-bold">✉️</span> <a className="hover:text-[#f59321]" href={`mailto:${a.email}`}>{a.email}</a></div>
                          <div><span className="font-bold">📞</span> <a className="hover:text-[#f59321]" href={`tel:${(a.phone || "").replace(/[^0-9]/g, "")}`}>{a.phone}</a></div>
                          <div><span className="font-bold">🚗</span> {a.vehicleType || "—"} · {a.licenseClass || "—"}</div>
                          <div><span className="font-bold">⏱</span> {a.availability || "—"} · {a.yearsDriving || "—"}</div>
                          {a.about && <div className="text-slate-500 italic pt-2">"{a.about}"</div>}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => updateApplicationStatus(a.id, "approved")}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-9 font-semibold"
                          >
                            <Icons.Check className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateApplicationStatus(a.id, "rejected")}
                            className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-full h-9 font-semibold"
                          >
                            <Icons.X className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* DRIVERS */}
          <TabsContent value="drivers" className="mt-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ADMIN_DRIVERS.map((d) => (
                <div key={d.id} className="bg-white rounded-2xl border border-slate-200 p-5 card-lift">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#0f2847] text-white font-display font-extrabold flex items-center justify-center">
                      {d.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-[#0f2847] truncate">{d.name}</div>
                      <div className="text-xs text-slate-500">{d.id}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${DRIVER_STATUS[d.status]}`}>
                      {d.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-center pt-4 border-t border-slate-100">
                    <div>
                      <div className="font-display text-xl font-extrabold text-[#0f2847]">{d.today}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Stops today</div>
                    </div>
                    <div>
                      <div className="font-display text-xl font-extrabold text-[#0f2847] flex items-center justify-center gap-1">
                        {d.rating}
                        <Icons.Star className="w-4 h-4 fill-[#f59321] text-[#f59321]" />
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Rating</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* REVENUE */}
          <TabsContent value="revenue" className="mt-5">
            <div className="grid lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="font-display font-bold text-[#0f2847] mb-1">Revenue · last 7 days</h3>
                <div className="font-display text-4xl font-extrabold text-[#0f2847] mb-5">$8,412</div>
                <div className="flex items-end gap-3 h-48">
                  {[42, 68, 55, 78, 90, 65, 84].map((v, i) => (
                    <div key={i} className="flex-1 h-full flex flex-col items-center gap-2">
                      <div className="w-full bg-slate-100 rounded-lg relative overflow-hidden flex-1 flex items-end">
                        <div
                          className="w-full bg-gradient-to-t from-[#f59321] to-[#fbbf24] rounded-lg transition-all duration-700"
                          style={{ height: `${v}%` }}
                        />
                      </div>
                      <div className="text-[10px] font-bold text-slate-500 uppercase">
                        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#0f2847] text-white rounded-2xl p-6 stripe-bg">
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-2">Top service</div>
                <div className="font-display text-2xl font-extrabold">Medical Rush</div>
                <div className="mt-1 text-slate-300 text-sm">42% of weekly revenue</div>
                <ul className="mt-5 space-y-3 text-sm">
                  {[
                    { label: "Medical Rush", pct: 42 },
                    { label: "Legal Same-Day", pct: 28 },
                    { label: "Standard", pct: 18 },
                    { label: "Pallet / Scheduled", pct: 12 }
                  ].map((r) => (
                    <li key={r.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span>{r.label}</span>
                        <span className="font-bold text-[#f59321]">{r.pct}%</span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-[#f59321]" style={{ width: `${r.pct}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-5 bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-display font-bold text-[#0f2847]">Customer invoices</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">
                  <tr>
                    <th className="px-6 py-3">Invoice</th>
                    <th className="px-6 py-3">Period</th>
                    <th className="px-6 py-3">Deliveries</th>
                    <th className="px-6 py-3 text-right">Amount</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {CUSTOMER_INVOICES.map((inv) => (
                    <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4 font-bold text-[#0f2847]">{inv.id}</td>
                      <td className="px-6 py-4 text-slate-700">{inv.period}</td>
                      <td className="px-6 py-4 text-slate-700">{inv.deliveries}</td>
                      <td className="px-6 py-4 text-right font-bold text-[#0f2847]">{inv.amount}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                          {inv.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CUSTOMERS */}
          <TabsContent value="customers" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-display font-bold text-[#0f2847]">Registered accounts (live from Firestore)</h3>
                <p className="text-xs text-slate-500 mt-0.5">All users signed up on the site — customers, drivers and admins.</p>
              </div>
              {users.length === 0 ? (
                <div className="p-16 text-center">
                  <Icons.Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-display font-bold text-[#0f2847]">No accounts yet</h4>
                  <p className="text-sm text-slate-500 mt-1">Or Firestore rules block listing — see below.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">
                      <tr>
                        <th className="px-6 py-3">User</th>
                        <th className="px-6 py-3">Role</th>
                        <th className="px-6 py-3">Contact</th>
                        <th className="px-6 py-3">Company</th>
                        <th className="px-6 py-3">Since</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => {
                        const roleClass =
                          u.role === "admin"
                            ? "bg-red-100 text-red-700"
                            : u.role === "driver"
                            ? "bg-[#f59321]/15 text-[#c46905]"
                            : "bg-emerald-100 text-emerald-700";
                        return (
                          <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-[#0f2847] text-white font-display font-extrabold text-xs flex items-center justify-center">
                                  {(u.name || u.email || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                                </div>
                                <div className="font-bold text-[#0f2847]">{u.name || "—"}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${roleClass}`}>
                                {u.role || "customer"}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-xs">
                              <a href={`mailto:${u.email}`} className="text-[#0f2847] hover:text-[#f59321] block">{u.email}</a>
                              {u.phone && <div className="text-slate-500 mt-0.5">{u.phone}</div>}
                            </td>
                            <td className="px-6 py-4 text-slate-700">{u.company || "—"}</td>
                            <td className="px-6 py-4 text-xs text-slate-500">{fmtDate(u.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* COMPLIANCE */}
          <TabsContent value="compliance" className="mt-5">
            <AdminCompliance />
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
